<template>
  <div class="gwp-product-item" :class="`gwp-product-item--${theme}`">
    <div class="gwp-product-item__content">
      <div class="gwp-product-item__image-wrap">
        <Skeleton v-if="isLoading || isImageLoading" height="100%" class="gwp-product-item__image" />
        <img
          v-show="!isLoading && !isImageLoading"
          :src="image"
          :alt="description"
          class="gwp-product-item__image"
          data-test-id="gwp-product-item-image"
          @load="isImageLoading = false"
          @error="isImageLoading = false"
        />
      </div>
      <div class="gwp-product-item__text-wrap">
        <slot name="title" :title="title">
          <Skeleton v-if="isLoading" height="20px" width="100%" />
          <button
            v-else
            class="gwp-product-item__title"
            :aria-label="`Quick view of ${title}`"
            :title="`Quick view of ${title}`"
            data-test-id="gwp-product-item-title"
            @click="$emit('open-quick-view')"
          >
            <span class="gwp-product-item__title-text">
              {{ title }}
            </span>
          </button>
        </slot>
        <span>
          <Skeleton v-if="isLoading" height="18px" width="100%" class="gwp-product-item__description-skeleton" />
          <span
            v-else-if="!!description"
            class="gwp-product-item__description"
            data-test-id="gwp-product-item-description"
            v-html="description"
          ></span>
        </span>
        <span>
          <Skeleton v-if="isLoading" height="18px" width="100%" class="gwp-product-item__status-skeleton" />
          <span v-else-if="alreadyInBag" class="gwp-product-item__status" data-test-id="gwp-product-item-status">
            <Icon :icon="csgConfirmationWithoutCircleGreen" class="gwp-product-item__status-icon" size="s" />
            <span
              >Item already in Bag —
              <a
                :href="REDIRECT_MAP.bag"
                target="_blank"
                rel="noopener noreferrer"
                class="link-med menu-link-body"
                @click="tagUpdateAnytimeLinkClickedOnce"
              >
                update anytime
              </a>
            </span>
          </span>
          <span v-else-if="spendMoreToUnlock" class="gwp-product-item__status" data-test-id="gwp-product-item-status"> Spend more to unlock </span>
        </span>
      </div>
    </div>

    <div v-if="!isLoading && $slots['swap-button']" class="gwp-product-item__cta-button">
      <slot v-if="isSwapButtonAvailable" name="swap-button"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch, ref } from 'vue';
import Skeleton from '@atomic-ui/skeleton/Skeleton';
import Icon from '@atomic-ui/icon/Icon';
import { csgConfirmationWithoutCircleGreen } from '@csgSvgIcons/icons';
import once from '~/utils/once';
import { REDIRECT_MAP } from '~/constants/common';
import { tagUpdateAnytimeLinkClicked } from '~/features/analytics/gwp';

defineEmits<{
  'open-quick-view': [];
}>();

const {
  title,
  description = '',
  image,
  theme = 'dark',
  isSwapButtonAvailable = false,
  isLoading = false,
  alreadyInBag = false,
  spendMoreToUnlock = false,
} = defineProps<{
  title: string;
  description?: string;
  image: string;
  theme?: 'light' | 'dark';
  isSwapButtonAvailable?: boolean;
  isLoading?: boolean;
  alreadyInBag?: boolean;
  spendMoreToUnlock?: boolean;
}>();

const tagUpdateAnytimeLinkClickedOnce = once(tagUpdateAnytimeLinkClicked);
const isImageLoading = ref(false);
watch(
  () => image,
  () => {
    isImageLoading.value = true;
  },
);
</script>

<style scoped lang="scss">
@import '~settingSCSS';
@import '@core/csg/src/scss/typography/typography-mixins.scss';

.gwp-product-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: rem-calc(8px);
  width: 100%;
  @include padding-top-xs;
  @include padding-bottom-xs;
  @include padding-left-xs;
  padding-right: rem-calc(12px);
  border-radius: 5px;

  &.gwp-product-item--dark {
    background-color: $background-color;
  }

  &.gwp-product-item--light {
    background-color: $white;
    border: 1px solid $gray-2-color;

    &.gwp-product-item--active {
      border-color: $black;
      outline: 2px solid $black;
      outline-offset: -1px;
    }
  }
}

.gwp-product-item__content {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: rem-calc(8px);
}

.gwp-product-item__image-wrap {
  flex: 0 0 60px;
  width: rem-calc(60px);
  height: rem-calc(78px);
}

.gwp-product-item__text-wrap {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}

.gwp-product-item__title {
  text-align: left;
  cursor: pointer;
  @include body(large);

  &:hover {
    text-decoration: underline;
  }
}

.gwp-product-item__title-text {
  @include truncate-multiline(2);
}

.gwp-product-item__description,
.gwp-product-item__status {
  color: $gray-1-color;
  @include truncate-multiline(2);
  &:not(:empty) {
    margin-top: rem-calc(4px);
  }
}

.gwp-product-item__description-skeleton,
.gwp-product-item__status-skeleton {
  margin-top: rem-calc(4px);
}

.gwp-product-item__cta-button {
  flex: 0 0 auto;
}

.gwp-product-item__status-icon {
  vertical-align: middle;
  margin-right: rem-calc(4px);
}
</style>


